import { MouseEvent, useCallback } from 'react'

import { Checkbox, Skeleton } from '@mui/material'
import { flexRender, HeaderGroup, Table } from '@tanstack/react-table'

import { ActionMenuTableCell, CheckboxTableCell, DynamicContentTableCell, HeadTableRow } from '@shared/components/Table'

type HeadRowProps<D> = {
  tableInstance: Table<D>
  row: HeaderGroup<D>
  enableSelection?: boolean

  columnWidth: {
    gridTemplateColumns: string
  }
  enableActionColumn?: boolean
  isInitialLoading?: boolean
}

const HeadRowRaw = <D,>({
  tableInstance,
  row,
  columnWidth,
  enableSelection,
  enableActionColumn,
  isInitialLoading,
}: HeadRowProps<D>) => {
  const handleSelection = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (e.shiftKey && tableInstance.getIsSomeRowsSelected()) {
        tableInstance.toggleAllRowsSelected(true)
      } else {
        if (tableInstance.getIsSomeRowsSelected() || tableInstance.getIsAllRowsSelected()) {
          tableInstance.toggleAllRowsSelected(false)
        } else {
          tableInstance.toggleAllRowsSelected(true)
        }
      }
    },
    [tableInstance],
  )

  return (
    <HeadTableRow height={tableInstance.options.meta?.sizing.headHeight || 56} columnWidth={columnWidth}>
      {enableSelection && (
        <CheckboxTableCell
          height={tableInstance.options.meta?.sizing.headHeight || 56}
          isSelected={tableInstance.getIsSomeRowsSelected()}
        >
          {isInitialLoading ? (
            <Skeleton sx={{ px: 1 }} />
          ) : (
            <Checkbox
              size="small"
              checked={tableInstance.getIsAllRowsSelected()}
              indeterminate={tableInstance.getIsSomeRowsSelected()}
              onClick={handleSelection}
            />
          )}
        </CheckboxTableCell>
      )}
      {row.headers.map((header) => (
        <DynamicContentTableCell
          key={header.id}
          component="th"
          colSpan={header.colSpan}
          height={tableInstance.options.meta?.sizing.headHeight || 56}
          horizontalAlignment={header.column.columnDef.meta?.align}
          horizontalPadding={header.column.columnDef.meta?.sizing?.px}
        >
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
        </DynamicContentTableCell>
      ))}
      {enableActionColumn && (
        <ActionMenuTableCell height={tableInstance.options.meta?.sizing.headHeight || 56} component="th" />
      )}
    </HeadTableRow>
  )
}

export const HeadRow = HeadRowRaw
